import { useEffect, useRef, useState } from 'react';
import { App, Button, Flex, Input, Space, Spin, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useWindowManager } from '@/shared/lib/windows';
import { translateApiError } from '@/shared/api/errorToMessage';
import {
  useCreateAiConversationMutation,
  useDeleteAiConversationMutation,
  useGetAiMessagesQuery,
  useListAiConversationsQuery,
  useSendAiMessageMutation,
} from '@/entities/ai-chat';
import type { AiConversationDto, AiMessageDto, SaleDraftToolResult } from '@/entities/ai-chat';
import { useDeleteDocumentMutation, useGetDocumentQuery, usePostDocumentMutation } from '@/entities/document';

export interface AiChatWindowProps {
  onClose: () => void;
}

// Держим в синхроне с backend CONVERSATION_IDLE_CLOSE_MS (ai-chat.service.ts) — здесь чисто для
// UI (disabled-состояние, подсказки), сервер перепроверяет это же правило перед записью сообщения.
const CONVERSATION_IDLE_CLOSE_MS = 60 * 60 * 1000;

function isConversationClosed(updatedAt: string, now: number): boolean {
  return now - new Date(updatedAt).getTime() > CONVERSATION_IDLE_CLOSE_MS;
}

function parseSaleDraft(message: AiMessageDto): SaleDraftToolResult | null {
  if (message.role !== 'tool' || message.toolName !== 'propose_sale_draft' || !message.content) return null;
  try {
    const parsed = JSON.parse(message.content) as Partial<SaleDraftToolResult>;
    return parsed.documentId && parsed.summary ? (parsed as SaleDraftToolResult) : null;
  } catch {
    return null;
  }
}

/** Карточка предложенного ИИ черновика продажи: сводка + «Открыть»/«Подтвердить и провести»/
 *  «Отмена». Сама следит за текущим статусом документа (`useGetDocumentQuery`) — после проведения
 *  или удаления в любом окне кнопки скрываются сами, без отдельной локальной синхронизации.
 *  `locked` — беседа закрыта (архив, §новое): «Подтвердить и провести»/«Отмена» скрыты, сам
 *  документ при этом не заблокирован — им можно управлять как обычно через список документов. */
function SaleDraftCard({ draft, locked }: { draft: SaleDraftToolResult; locked: boolean }) {
  const { message: toast } = App.useApp();
  const windowManager = useWindowManager();
  const { data: doc } = useGetDocumentQuery(draft.documentId);
  const [postDocument, { isLoading: posting }] = usePostDocumentMutation();
  const [deleteDocument, { isLoading: cancelling }] = useDeleteDocumentMutation();

  async function handleConfirm() {
    if (!doc) return;
    try {
      await postDocument({ id: doc.id, body: { rowVersion: doc.rowVersion } }).unwrap();
      toast.success(`Продажа №${draft.summary.number} проведена`);
    } catch (e) {
      toast.error(translateApiError(e));
    }
  }

  async function handleCancel() {
    if (!doc) return;
    try {
      await deleteDocument({ id: doc.id, body: { rowVersion: doc.rowVersion } }).unwrap();
      toast.success('Черновик отменён');
    } catch (e) {
      toast.error(translateApiError(e));
    }
  }

  const status = doc?.deletedAt ? 'DELETED' : doc?.status;

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 0, padding: 8, background: '#fafafa', fontSize: 12 }}>
      <Flex justify="space-between" align="center">
        <Typography.Text strong style={{ fontSize: 12 }}>
          Черновик продажи №{draft.summary.number} · {draft.summary.totalAmount} ₽
        </Typography.Text>
        {status && status !== 'DRAFT' ? <Tag style={{ borderRadius: 0 }}>{statusLabel(status)}</Tag> : null}
      </Flex>
      {draft.summary.counterparty ? <div>Клиент: {draft.summary.counterparty}</div> : <div style={{ color: '#8c8c8c' }}>Клиент не выбран</div>}
      <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
        {draft.summary.lines.map((line, i) => (
          <li key={i}>
            {line.category ?? '—'}: {line.boxes ?? '—'} кор. × {line.unitPrice ?? '—'} = {line.lineAmount}
          </li>
        ))}
      </ul>
      <Space size={8}>
        <Button
          size="small"
          onClick={() => windowManager.open({ kind: 'document', type: 'SALE', id: draft.documentId })}
        >
          Открыть
        </Button>
        {!locked && (!status || status === 'DRAFT') && (
          <>
            <Button size="small" type="primary" loading={posting} disabled={!doc} onClick={() => void handleConfirm()}>
              Подтвердить и провести
            </Button>
            <Button size="small" danger loading={cancelling} disabled={!doc} onClick={() => void handleCancel()}>
              Отмена
            </Button>
          </>
        )}
      </Space>
    </div>
  );
}

function statusLabel(status: string): string {
  if (status === 'POSTED') return 'Проведён';
  if (status === 'CANCELLED') return 'Отменён';
  if (status === 'DELETED') return 'Удалён';
  return status;
}

function MessageBubble({ role, text }: { role: 'user' | 'assistant'; text: string }) {
  const isUser = role === 'user';
  return (
    <Flex justify={isUser ? 'flex-end' : 'flex-start'}>
      <div
        style={{
          maxWidth: '85%',
          padding: '6px 8px',
          fontSize: 12,
          background: isUser ? '#e6f4ff' : '#f5f5f5',
          border: '1px solid #d9d9d9',
          whiteSpace: 'pre-wrap',
        }}
      >
        {text}
      </div>
    </Flex>
  );
}

function conversationLabel(c: AiConversationDto): string {
  return c.title ?? 'Новый чат';
}

function formatConversationTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Слева от чата — список предыдущих бесед (сортирован бэкендом по updatedAt, последняя активная
 *  сверху), кнопка создания новой и удаление беседы (без восстановления). Беседа без сообщений час
 *  — архив: помечена тегом, в ней уже нельзя писать (см. `isConversationClosed`). */
function ConversationSidebar({
  conversations,
  selectedId,
  now,
  onSelect,
  onCreate,
  onDelete,
  creating,
}: {
  conversations: AiConversationDto[];
  selectedId: string | null;
  now: number;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
  creating: boolean;
}) {
  return (
    <Flex vertical style={{ width: 200, flex: '0 0 auto', height: '100%', borderRight: '1px solid #d9d9d9', marginRight: 8 }}>
      <div style={{ paddingRight: 8, paddingBottom: 8 }}>
        <Button block size="small" icon={<PlusOutlined />} loading={creating} onClick={onCreate} style={{ borderRadius: 0 }}>
          Новый чат
        </Button>
      </div>
      <Flex vertical style={{ flex: '1 1 auto', overflow: 'auto', borderTop: '1px solid #d9d9d9' }}>
        {conversations.map((c) => {
          const active = c.id === selectedId;
          const closed = isConversationClosed(c.updatedAt, now);
          return (
            <Flex
              key={c.id}
              align="center"
              justify="space-between"
              gap={4}
              style={{ padding: '6px 2px 6px 8px', background: active ? '#e6f4ff' : undefined, borderBottom: '1px solid #f0f0f0' }}
            >
              <div role="button" onClick={() => onSelect(c.id)} style={{ flex: '1 1 auto', minWidth: 0, fontSize: 12, cursor: 'pointer' }} title={conversationLabel(c)}>
                <Flex align="center" gap={4}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conversationLabel(c)}</div>
                  {closed ? (
                    <Tag style={{ borderRadius: 0, margin: 0, fontSize: 10, lineHeight: '14px', flex: '0 0 auto' }}>архив</Tag>
                  ) : null}
                </Flex>
                <div style={{ color: '#8c8c8c', fontSize: 11 }}>{formatConversationTimestamp(c.updatedAt)}</div>
              </div>
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                title="Удалить беседу"
                aria-label="Удалить беседу"
                style={{ flex: '0 0 auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(c.id);
                }}
              />
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}

/** Чат с ИИ-ассистентом: пока умеет оформлять черновик продажи по описанию на русском — то же
 *  плавающее окно, что у документов/отчётов, открывается из сайдбара owner-интерфейса. Слева —
 *  список предыдущих бесед пользователя в этом магазине и кнопка «Новый чат»; при первом открытии
 *  без единой беседы одна создаётся автоматически. История каждой беседы хранится на бэкенде и
 *  переживает закрытие окна/перезагрузку страницы. */
export function AiChatWindow({ onClose }: AiChatWindowProps) {
  const { message: toast, modal } = App.useApp();
  const windowManager = useWindowManager();
  const { data: conversations, isFetching: loadingConversations } = useListAiConversationsQuery();
  const [createConversation, { isLoading: creatingConversation }] = useCreateAiConversationMutation();
  const [deleteConversation] = useDeleteAiConversationMutation();
  // Явный выбор (клик по беседе в списке, «Новый чат», либо беседа, созданная автоматически ниже)
  // — пока его нет, берём самую свежую беседу из списка (он отсортирован бэкендом по updatedAt).
  // Деривация, а не setState в эффекте — иначе рефетч списка после каждого сообщения дёргал бы
  // выбор обратно на «самую свежую», даже когда пользователь уже открыл другую беседу.
  const [explicitConversationId, setExplicitConversationId] = useState<string | null>(null);
  const conversationId = explicitConversationId ?? (conversations && conversations.length > 0 ? conversations[0]!.id : null);
  const requestedInitialCreate = useRef(false);

  // Тикает раз в минуту, чтобы «закрыта час без сообщений» подхватывалось само, даже если окно
  // просто открыто и простаивает — без этого состояние обновилось бы только на следующий ререндер
  // от другого события (сообщение, рефетч списка и т.п.).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const selectedConversation = conversations?.find((c) => c.id === conversationId) ?? null;
  const closed = selectedConversation ? isConversationClosed(selectedConversation.updatedAt, now) : false;

  // Единственный случай, который правда требует эффекта: у пользователя вообще нет ни одной беседы
  // — тогда одна создаётся автоматически при первом открытии окна.
  useEffect(() => {
    if (conversationId || loadingConversations || !conversations || conversations.length > 0 || requestedInitialCreate.current) return;
    requestedInitialCreate.current = true;
    createConversation()
      .unwrap()
      .then((c) => setExplicitConversationId(c.id))
      .catch((e: unknown) => toast.error(translateApiError(e)));
  }, [conversations, loadingConversations, conversationId, createConversation, toast]);

  const { data: messages, refetch: refetchMessages } = useGetAiMessagesQuery(conversationId ?? '', { skip: !conversationId });
  const [sendMessage, { isLoading: sending }] = useSendAiMessageMutation();
  const [text, setText] = useState('');
  // Своё сообщение должно появиться в чате сразу по нажатию «Отправить», а не только после ответа
  // ИИ (ответ может идти несколько секунд — внутри цикл function-calling). Очищается после
  // `refetchMessages()` (а не сразу по resolve мутации), чтобы не было кадра, где сообщение уже
  // пропало, а из перечитанного списка ещё не пришло — `invalidatesTags` сам по себе рефетч не ждёт.
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages?.length, pendingUserMessage, sending]);

  async function handleSend() {
    const value = text.trim();
    if (!value || !conversationId || sending || closed) return;
    setText('');
    setPendingUserMessage(value);
    try {
      const result = await sendMessage({ conversationId, text: value }).unwrap();
      await refetchMessages();
      // Черновик, который только что предложил ИИ, открывается сразу — так же, как кнопка
      // «Открыть» на карточке черновика (см. SaleDraftCard), владельцу не нужен лишний клик,
      // чтобы проверить его перед проведением.
      if (result.draftDocument) {
        windowManager.open({ kind: 'document', type: 'SALE', id: result.draftDocument.id });
      }
    } catch (e) {
      toast.error(translateApiError(e));
      setText(value); // не заставляем переписывать заново
    } finally {
      setPendingUserMessage(null);
    }
  }

  async function handleNewChat() {
    try {
      const created = await createConversation().unwrap();
      setExplicitConversationId(created.id);
      setText('');
    } catch (e) {
      toast.error(translateApiError(e));
    }
  }

  function handleSelectConversation(id: string) {
    if (id === conversationId) return;
    setExplicitConversationId(id);
    setText('');
  }

  function handleDeleteConversation(id: string) {
    modal.confirm({
      title: 'Удалить беседу?',
      content: 'История переписки будет удалена без возможности восстановления.',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteConversation(id).unwrap();
          // Удалили активную беседу — сбрасываем явный выбор, список после рефетча сам подставит
          // следующую по свежести (или окажется пустым, тогда доступна только «Новый чат»).
          if (id === conversationId) setExplicitConversationId(null);
          toast.success('Беседа удалена');
        } catch (e) {
          toast.error(translateApiError(e));
        }
      },
    });
  }

  return (
    <FloatingWindow kind="aiChat" title="ИИ-ассистент" onClose={onClose}>
      <Flex style={{ height: '100%' }}>
        <ConversationSidebar
          conversations={conversations ?? []}
          selectedId={conversationId}
          now={now}
          onSelect={handleSelectConversation}
          onCreate={() => void handleNewChat()}
          onDelete={handleDeleteConversation}
          creating={creatingConversation}
        />
        <Flex vertical gap={8} style={{ flex: '1 1 auto', minWidth: 0, height: '100%' }}>
          {closed ? (
            <Flex align="center" gap={6} style={{ flex: '0 0 auto', padding: '4px 8px', background: '#fafafa', border: '1px solid #d9d9d9', fontSize: 12, color: '#8c8c8c' }}>
              <Tag style={{ borderRadius: 0, margin: 0 }}>Архив</Tag>
              Беседа закрыта — прошёл час с последнего сообщения, доступна только для просмотра
            </Flex>
          ) : null}
          <Flex vertical gap={6} ref={listRef} style={{ flex: '1 1 auto', overflow: 'auto' }}>
            {!conversationId ? (
              <Spin size="small" />
            ) : messages?.length === 0 && !pendingUserMessage ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Опишите продажу своими словами — например «продал 3 коробки чипсов Лейс по 420». Я найду товар и предложу черновик на подтверждение.
              </Typography.Text>
            ) : (
              messages?.map((m) => {
                if (m.role === 'user' || m.role === 'assistant') {
                  if (!m.content) return null;
                  return <MessageBubble key={m.id} role={m.role} text={m.content} />;
                }
                const draft = parseSaleDraft(m);
                return draft ? <SaleDraftCard key={m.id} draft={draft} locked={closed} /> : null;
              })
            )}
            {pendingUserMessage ? <MessageBubble role="user" text={pendingUserMessage} /> : null}
            {sending ? (
              <Flex justify="flex-start">
                <div style={{ padding: '6px 8px', fontSize: 12, background: '#f5f5f5', border: '1px solid #d9d9d9' }}>
                  <Spin size="small" /> печатает…
                </div>
              </Flex>
            ) : null}
          </Flex>
          <Flex gap={8} align="flex-end" style={{ flex: '0 0 auto', paddingTop: 8, borderTop: '1px solid #d9d9d9' }}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              placeholder={closed ? 'Беседа закрыта — создайте новый чат' : 'Например: продал 2 блока Coca-Cola по 350'}
              value={text}
              disabled={!conversationId || closed}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <Button type="primary" icon={<SendOutlined />} loading={sending} disabled={!conversationId || closed || !text.trim()} onClick={() => void handleSend()} />
          </Flex>
        </Flex>
      </Flex>
    </FloatingWindow>
  );
}
